import {
  ConversationId,
  InternalConversation,
  InternalConversationCounters,
  InternalConversationExportRow,
  InternalConversationFilter,
  InternalConversationSummary,
  Region,
} from "platformed-client/types";
import {platformedClient} from "../index";

type RegionError = {region: Region; error: unknown};
type Row = {created_at: string; conversation_id: string};

// Fan out a per-region fetch in parallel and merge the results, newest
// first. Per-region failures are surfaced but non-fatal: a single region
// being down does not block the others.
async function fanOut<T extends Row>(
  regions: Region[],
  fetchRegion: (region: Region) => Promise<T[]>,
): Promise<{rows: T[]; errors: RegionError[]}> {
  const results = await Promise.allSettled(regions.map(fetchRegion));
  const rows: T[] = [];
  const errors: RegionError[] = [];
  for (const [i, result] of results.entries()) {
    if (result.status === "fulfilled") rows.push(...result.value);
    else errors.push({region: regions[i], error: result.reason});
  }
  rows.sort((a, b) => {
    if (a.created_at > b.created_at) return -1;
    if (a.created_at < b.created_at) return 1;
    if (a.conversation_id > b.conversation_id) return -1;
    if (a.conversation_id < b.conversation_id) return 1;
    return 0;
  });
  return {rows, errors};
}

// Drop null / undefined / empty entries so they don't serialise as
// `?account_id=null` and confuse the backend's nullable-bind filter.
function cleanQuery(args: InternalConversationFilter): Record<string, unknown> {
  return Object.fromEntries(Object.entries(args).filter(([, v]) => v != null && v !== ""));
}

/** Cross-region list of conversations for the dashboard's display page. */
export async function listConversations(
  regions: Region[],
  args: InternalConversationFilter,
): Promise<{conversations: InternalConversationSummary[]; errors: RegionError[]}> {
  const {rows, errors} = await fanOut(regions, region =>
    platformedClient.internalRequest("get", "/region/{region}/api/internal/conversations", {
      path: {region},
      query: cleanQuery(args),
    }),
  );
  return {conversations: rows, errors};
}

/**
 * The projected rows the CSV export is built from: every conversation matching
 * `args` in each region, up to the server's own safety cap (the `limit` in
 * `args` is ignored). `truncated` is true if any region hit that cap, so the
 * caller can warn instead of presenting a short CSV as complete.
 */
export async function exportConversations(
  regions: Region[],
  args: InternalConversationFilter,
): Promise<{rows: InternalConversationExportRow[]; truncated: boolean; errors: RegionError[]}> {
  const results = await Promise.allSettled(
    regions.map(region =>
      platformedClient.internalRequest("get", "/region/{region}/api/internal/conversations/export", {
        path: {region},
        query: cleanQuery(args),
      }),
    ),
  );
  const rows: InternalConversationExportRow[] = [];
  const errors: RegionError[] = [];
  let truncated = false;
  for (const [i, result] of results.entries()) {
    if (result.status === "fulfilled") {
      rows.push(...result.value.rows);
      truncated ||= result.value.truncated;
    } else {
      errors.push({region: regions[i], error: result.reason});
    }
  }
  rows.sort((a, b) => {
    if (a.created_at > b.created_at) return -1;
    if (a.created_at < b.created_at) return 1;
    if (a.conversation_id > b.conversation_id) return -1;
    if (a.conversation_id < b.conversation_id) return 1;
    return 0;
  });
  return {rows, truncated, errors};
}

/**
 * Volatile counters for a batch of conversations in one region — the refresh
 * path the live dashboard coalesces per-row `EventAppended`s into. A GET so
 * the server runs it read-only. IDs absent from the region are omitted.
 */
export async function loadConversationCounters(
  region: Region,
  conversationIds: ConversationId[],
): Promise<InternalConversationCounters[]> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/internal/conversations/counters", {
    path: {region},
    query: {ids: conversationIds.join(",")},
  });
}

/** Look up a single conversation in a specific region. */
export async function getConversation(region: Region, conversationId: ConversationId): Promise<InternalConversation> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/internal/conversations/{conversation_id}",
    {path: {region, conversation_id: conversationId}},
  );
}

/**
 * Retire a conversation so the caller's next attempt for the same
 * `(parent, step)` starts a fresh conversation instead of reusing this one.
 * Returns the updated conversation.
 */
export async function retireConversation(
  region: Region,
  conversationId: ConversationId,
): Promise<InternalConversation> {
  return await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/internal/conversations/{conversation_id}/retire",
    {path: {region, conversation_id: conversationId}},
  );
}
